import { v4 as uuidv4 } from "uuid";
import { LocalChunkSize, readRemoteFile } from "react-papaparse";

import { api } from "api";
import { Utils } from "utils";
import { IPlace } from "types";
import { ThunkType } from "store/types";

import { convertToPlaces } from "./helpers";
import {
  setLoadingID,
  setCountedIDs,
  setLocalFiles,
  setUploadedIDs,
  setGroupedByCategories,
} from "./slice";

export const createLocalFilesThunk =
  (files: File[]): ThunkType<Promise<void>> =>
  async (dispatch, getStore) => {
    const localFiles = getStore().placesUpload.files;

    dispatch(
      setLocalFiles([
        ...localFiles,
        ...files.map((item) => ({ data: item, id: uuidv4() })),
      ])
    );

    return Promise.resolve();
  };

export const deleteLocalFileThunk =
  (id: string): ThunkType<void> =>
  (dispatch, getStore) => {
    const localFiles = getStore().placesUpload.files;
    dispatch(setLocalFiles(localFiles.filter((file) => file.id !== id)));
  };

const CHUNK_SIZE = LocalChunkSize * 5; // 50 MB

const papaparseConfig = Object.freeze({
  header: true,
  worker: true,
  download: true,
  skipEmptyLines: true,
  chunkSize: CHUNK_SIZE,
});

const parseCsvAsync = async (file: File): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    const uri = URL.createObjectURL(file);

    let data: any[] = [];

    readRemoteFile(uri, {
      ...papaparseConfig,
      chunk: (results) => {
        data = [...data, ...results.data];
      },
      complete: () => {
        URL.revokeObjectURL(uri);
        resolve(data);
      },
      error: (err) => {
        URL.revokeObjectURL(uri);
        reject(err);
      },
    });
  });
};

export const groupByCategoriesThunk =
  (fileID: string, places: IPlace[]): ThunkType<Promise<void>> =>
  (dispatch, getStore) => {
    const categories = getStore().categories.list;
    const current = getStore().placesUpload.groupedByCategories;

    const grouped = Utils.Places.groupByCategories(categories, places);
    const counted = Utils.Places.countInGroups(grouped);

    dispatch(setGroupedByCategories({ ...current, [fileID]: counted }));
    return Promise.resolve();
  };

export const getCountByCategoriesThunk =
  (): ThunkType<Promise<void>> => async (dispatch, getStore) => {
    try {
      const localFiles = getStore().placesUpload.files;
      const countedIDs = getStore().placesUpload.countedIDs;

      const files = localFiles.filter((item) => !countedIDs.includes(item.id));

      for (const item of files) {
        const { id, data: file } = item;
        dispatch(setLoadingID(id));

        const response = await parseCsvAsync(file);
        const places = await convertToPlaces(response);
        console.log({ places });

        await dispatch(groupByCategoriesThunk(id, places));
        dispatch(setCountedIDs([...countedIDs, id]));
      }

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    } finally {
      dispatch(setLoadingID(undefined));
    }
  };

export const uploadToDbThunk =
  (): ThunkType<Promise<void>> => async (dispatch, getStore) => {
    try {
      const categories = getStore().categories.list;
      const localFiles = getStore().placesUpload.files;
      const completedIds = getStore().placesUpload.uploadedIDs;

      const files = localFiles.filter(
        (item) => !completedIds.includes(item.id)
      );

      for (const item of files) {
        const { id, data: file } = item;
        dispatch(setLoadingID(id));

        console.log({ file: file.name });

        const response = await parseCsvAsync(file);
        const places = await convertToPlaces(response);
        console.log({ places: places.length });

        const filtered = Utils.Places.filterByCategories(categories, places);
        console.log({ filtered: filtered.length });

        const grouped = Utils.Places.groupByCategories(categories, places);
        const counted = Utils.Places.countInGroups(grouped);
        console.log({ grouped: counted });

        const chunks = Utils.Helpers.chunkArray(filtered, 350);
        console.log({ chunks: chunks.length });

        const uploadChunks = Utils.Helpers.chunkArray(chunks, 10);

        for (const [index, uploadChunk] of uploadChunks.entries()) {
          console.log({ chunk: index, status: "START" });

          await Promise.all(
            uploadChunk.map(async (chunk, i) => {
              console.log({ innerChunk: i, status: "START" });
              await api.https.uploadDumpToDb(JSON.stringify(chunk));
              console.log({ innerChunk: i, status: "FINISH" });
            })
          );
          console.log({ chunk: index, status: "FINISH" });
        }

        dispatch(setUploadedIDs([...completedIds, id]));
      }

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    } finally {
      dispatch(setLoadingID(undefined));
    }
  };
